import React, { SyntheticEvent, useMemo, useState } from "react";

import { Select } from "@at/design-system-elements";
import { ThemeType, TypeFiesta } from "@at/design-system-foundation";
import { Banner } from "@at/design-system-patterns";
import { getChannelSingular } from "@at/formatter-utils";
import {
  dataAttrGUI,
  dataAttrTestId,
} from "@at/formatter-utils/lib/data-attr-utils/data-attr-utils";

import { PublicChargingExplainedModal } from "./public-charging-explained.modal";
import { ChargerLocationType } from "../../../../__generated__/graphql-types";
import { PublicChargingSvg } from "../../../../svgs/electric/public-charging-svg";
import { ChargingExplainedProps } from "../../../../types/chargin-data.types";
import { useChannel } from "../../../../utils/hooks/use-channel";
import { useTracking } from "../../../../utils/tracking/use-tracking";
import {
  ElectricWrapperStyled,
  TypeOfCharging,
  InputIconLabelWrapper,
  ElectricIcon,
} from "../charging.styles";
import { FullCharge, TopUpCharge } from "../type-of-charge";

export const PublicCharging = ({ chargers }: ChargingExplainedProps) => {
  const { trackEvent } = useTracking();
  const channel = useChannel();
  const channelSingular = getChannelSingular(channel);

  const publicChargers = useMemo(
    () =>
      chargers?.filter(
        (c) => c?.chargerLocation === ChargerLocationType.PUBLIC,
      ) || [],
    [chargers],
  );
  const defaultPublicCharger = publicChargers.find((charger) =>
    /\b50 kW/.test(charger?.description || ""),
  );

  const [selectedCharger, setSelectedCharger] = useState(
    defaultPublicCharger || publicChargers[0],
  );

  if (!chargers || chargers?.length === 0) {
    return <></>;
  }

  const handleChargerChange = (event: SyntheticEvent) => {
    const eventTarget = event.target as HTMLInputElement;
    const description = eventTarget.value;
    const selectedPublicCharger = publicChargers.find(
      (c) => c?.description === description,
    );
    trackEvent({ trackingLabel: "public-charger-dropdown-change" });
    if (selectedPublicCharger) {
      setSelectedCharger(selectedPublicCharger);
    }
  };

  return (
    <ElectricWrapperStyled data-gui="public-charging">
      <InputIconLabelWrapper>
        <ElectricIcon title="" svgContent={PublicChargingSvg}></ElectricIcon>
        <TypeFiesta className="electric-label" as="span" weight="500">
          Public Charging
        </TypeFiesta>
      </InputIconLabelWrapper>
      {!publicChargers ||
        (publicChargers.length === 0 && (
          <Banner variation={"info"} type={"in-panel"}>
            We don&apos;t have the public charging speed information for this{" "}
            {channelSingular} yet, but you are likely to be able to charge this{" "}
            {channelSingular} at public charging points.
          </Banner>
        ))}

      {publicChargers?.length > 0 && (
        <>
          <Select
            options={publicChargers?.map((charger) => ({
              value: charger?.description || "",
              displayValue: charger?.description || "",
            }))}
            label="Public Charging"
            hideLabel={true}
            id="publicChargingStation"
            name="Public charging"
            attributes={{
              onChange: handleChargerChange,
              [dataAttrTestId]: "publicChargers",
              [dataAttrGUI]: "public-charging-station",
            }}
            defaultValue={defaultPublicCharger?.description}
            themeType={ThemeType.COMFORT}
          />
          {selectedCharger && (
            <TypeOfCharging>
              <FullCharge charger={selectedCharger} />
              <TopUpCharge charger={selectedCharger} />
            </TypeOfCharging>
          )}
        </>
      )}
      <PublicChargingExplainedModal />
    </ElectricWrapperStyled>
  );
};
